import axios from 'axios';

// Use relative URL so it works with ngrok and other proxies
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// The session lives in an httpOnly cookie set by the server — no token in
// localStorage, nothing for a script on the page to steal
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Handle auth errors. ONLY a 401 means "logged out" — a 429 (tab burst) or a
// 5xx must never wipe the session, or every other tab renders login too.
// No redirect while already on /login: a failed login attempt is itself a 401
// and must not reload the page.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('user');
      // Remember the page so login can send the user back to it
      const here = `${window.location.pathname}${window.location.search}`;
      window.location.href = here === '/'
        ? '/login'
        : `/login?next=${encodeURIComponent(here)}`;
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) =>
  api.post('/auth/login', { username, password });

export const logout = () => api.post('/auth/logout');

export const getMe = () => api.get('/auth/me');

export const getMyAssignments = () => api.get('/auth/my-assignments');

export const changePassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', { currentPassword, newPassword });

// Inventory
export const getInventory = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.append('page', filters.page);
  if (filters.limit !== undefined) params.append('limit', filters.limit);
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  if (filters.performer) params.append('performer', filters.performer);
  if (filters.venue) params.append('venue', filters.venue);
  if (filters.tag) params.append('tag', filters.tag);
  if (filters.antiTag) params.append('antiTag', filters.antiTag);
  // Any-of tag lists, comma-joined (a tag never holds a comma or a space)
  if (filters.tags?.length) params.append('tags', [].concat(filters.tags).join(','));
  if (filters.antiTags?.length) params.append('antiTags', [].concat(filters.antiTags).join(','));
  if (filters.listed) params.append('listed', filters.listed); // 'true' | 'false'
  if (filters.purchaseDateFrom) params.append('purchaseDateFrom', filters.purchaseDateFrom);
  if (filters.purchaseDateTo) params.append('purchaseDateTo', filters.purchaseDateTo);
  // Only these events (an empty array sends `eventIds=`: matches nothing)
  if (filters.eventIds !== undefined) params.append('eventIds', [].concat(filters.eventIds).join(','));
  const queryString = params.toString();
  return api.get(`/inventory${queryString ? '?' + queryString : ''}`);
};

export const getInventoryItem = (id) => api.get(`/inventory/${id}`);

// Admin forensics: Skybox B, Vivid L, what-if and the POS price trail for one
// listing (API Debug panel). Four session-token Skybox calls - on demand only.
export const getSkyboxHistory = (id) => api.get(`/inventory/${id}/skybox-history`, { timeout: 60000 });

export const getEventPricing = (eventId, options = {}) => api.get(`/inventory/events/${eventId}/pricing`, options);

// Slim own-listings L/B read for the post-write L-price poller. maxAge (ms,
// server floor 3 s) bypasses an older cached Skybox payload.
export const getEventLPrices = (eventId, maxAge = 3000) =>
  api.get(`/inventory/events/${eventId}/lprices`, { params: { maxAge }, timeout: 60000 });

// maxAge (ms, server floor 10 s) bypasses the 2-minute sales cache (Refresh button)
export const getEventSales = (eventId, { maxAge } = {}) =>
  api.get(`/inventory/events/${eventId}/sales`, maxAge ? { params: { maxAge } } : undefined);

// Warm inventory cache freshness: { loaded, version, asOf, syncedAgoSeconds, ageSeconds, refreshing, degraded }
export const getInventorySyncStatus = () => api.get('/inventory/sync-status');

export const getEventNotes = (eventId) => api.get(`/inventory/events/${eventId}/notes`);

export const updateEventNotes = (eventId, notes) =>
  api.put(`/inventory/events/${eventId}/notes`, { notes });

export const getMyPerformerNotes = () => api.get('/inventory/performer-notes');

export const updateMyPerformerNotes = (notes) =>
  api.put('/inventory/performer-notes', { notes });

export const getMyEventNotes = () => api.get('/inventory/event-notes');

// suggestionId (optional) tells the backend this edit acted on an AI
// suggestion, so it records accepted / modified telemetry.
export const updatePrice = (id, newPrice, { vividListingId, zone, suggestionId, expectedCurrentPrice } = {}) =>
  api.put(`/inventory/${id}/price`, {
    newPrice, vividListingId, zone, suggestionId,
    // Stale-price guard (409): only sent when the caller actually knows the
    // row's current B — an omitted value must never read as "expected $0"
    ...(Number.isFinite(expectedCurrentPrice) ? { expectedCurrentPrice } : {}),
  });

// Serial server-side (one Skybox GET+PUT per item) — allow up to 3 min.
// source (optional, whitelisted server-side: 'lion_auto_ladder') labels the
// batch in the activity log. tagUnchanged (optional, a ladder Apply only):
// inventory ids already at their price, tagged `lion` once, never written.
export const bulkUpdatePrices = (updates, { source, tagUnchanged } = {}) =>
  api.put('/inventory/bulk-price', {
    updates,
    ...(source ? { source } : {}),
    ...(Array.isArray(tagUnchanged) && tagUnchanged.length ? { tagUnchanged } : {}),
  }, { timeout: 180000 });

// AI assistant
export const getAiStatus = () => api.get('/ai/status');

export const getEventAi = (eventId) => api.get(`/ai/events/${eventId}`);

// Starts the two-pass model pipeline (fresh comparables → analyze → verify)
// and returns 202 at once; poll getAnalyzeStatus for the result. The run
// takes 30 s–3 min, past nginx's 60 s proxy timeout, hence the split.
// `directive` may be a string or { directive?, targets?: inventoryId[] } —
// targets puts the assistant in initial-pricing mode for those listings.
export const analyzeEventAi = (eventId, directive) => {
  const body = {};
  if (typeof directive === 'string' && directive.trim()) body.directive = directive.trim();
  else if (directive && typeof directive === 'object') {
    if (directive.directive) body.directive = String(directive.directive);
    if (Array.isArray(directive.targets) && directive.targets.length) body.targets = directive.targets.map(Number);
  }
  return api.post(`/ai/events/${eventId}/analyze`, body, { timeout: 30000 });
};
export const getAnalyzeStatus = (eventId) => api.get(`/ai/events/${eventId}/analyze/status`, { timeout: 30000 });

export const askEventAi = (eventId, question) =>
  api.post(`/ai/events/${eventId}/ask`, { question }, { timeout: 180000 });

export const acceptAiSuggestion = (id, { price, vividListingId, zone } = {}) =>
  api.post(`/ai/suggestions/${id}/accept`, {
    ...(price != null ? { price } : {}),
    ...(vividListingId ? { vividListingId } : {}),
    ...(zone != null ? { zone } : {}),
  });

// { reasonCode?, note?, source? } — reasonCode is one of utils/aiFeedback
// REASON_CODES. With a code the server also records it as this user's "wrong"
// feedback; source 'bulk_dismiss' marks it as coming from the Price with AI
// review modal. A bare string is still accepted as the legacy free-text reason.
export const dismissAiSuggestion = (id, opts = {}) => {
  if (typeof opts === 'string') return api.post(`/ai/suggestions/${id}/dismiss`, { reason: opts });
  const { reasonCode, note, source } = opts || {};
  return api.post(`/ai/suggestions/${id}/dismiss`, {
    ...(reasonCode ? { reasonCode } : {}),
    ...(note ? { note: String(note).slice(0, 200) } : {}),
    ...(source === 'bulk_dismiss' ? { source } : {}),
  });
};

// Rate a suggestion (any status). Records feedback ONLY — never writes a price
// to Skybox. body { verdict: 'right'|'wrong', reasonCode? (required iff wrong),
// preferredPrice? (> 0), note? (≤ 300) } → { success, feedback: FeedbackView }
export const rateAiSuggestion = (id, { verdict, reasonCode, preferredPrice, note } = {}) =>
  api.post(`/ai/suggestions/${id}/feedback`, {
    verdict,
    ...(verdict === 'wrong' && reasonCode ? { reasonCode } : {}),
    ...(preferredPrice != null ? { preferredPrice } : {}),
    ...(note ? { note: String(note).slice(0, 300) } : {}),
  });

export const getAiAttention = () => api.get('/ai/attention');

export const getAiConfig = () => api.get('/ai/config');

export const updateAiConfig = (updates) => api.put('/ai/config', updates);

export const getAiTelemetry = () => api.get('/ai/telemetry');

export const runAiMaintenance = () => api.post('/ai/maintenance', {}, { timeout: 120000 });

export const runAiSweepTick = () => api.post('/ai/sweep', {}, { timeout: 300000 });

// One sales-sync tick (account-wide invoices → sales_history, sale ↔ listing
// matching). Serial Skybox calls server-side — allow 3 min.
export const runSalesSync = () => api.post('/ai/sales-sync', {}, { timeout: 180000 });

// TicketMetric sold data (Compare panel). Staff only; pricers are scoped to
// their events. requestTicketMetric never waits on TicketMetric: 200 = the
// stored view, 202 = { status: 'queued'|'running'|'paused', position, ... }.
// `sections` (our display names) go in as a JSON array so the server can
// match them to TicketMetric's section names.
export const requestTicketMetric = (eventId, { force = false } = {}) =>
  api.post(`/ticketmetric/events/${eventId}/request`, force ? { force: true } : {});

export const getTicketMetricEvent = (eventId, sections) =>
  api.get(`/ticketmetric/events/${eventId}`,
    Array.isArray(sections) && sections.length ? { params: { sections: JSON.stringify(sections) } } : undefined);

// body: { tmetricId } or { url } — saved for everyone, queues a pull
export const linkTicketMetricEvent = (eventId, body) =>
  api.post(`/ticketmetric/events/${eventId}/link`, body);

// "Wrong event?": unlink, never auto-link that event again, search again
export const unlinkTicketMetricEvent = (eventId) =>
  api.delete(`/ticketmetric/events/${eventId}/link`);

// Picker search; the server waits up to 40 s for the queue
export const searchTicketMetricEvents = (eventId, q) =>
  api.post(`/ticketmetric/events/${eventId}/search`, { q }, { timeout: 60000 });

// Lion Auto (staff; pricers only for events they can price). Groups are
// DB-only and cheap, except a new group sent with no ranks and an Approve at
// the Smart sort spot: those two and the two match calls read the event's
// Skybox pricing (5-minute server cache; maxAge in ms, server floor 10 s,
// forces a fresher read) and sit behind the per-user skyboxHeavy limiter;
// the match calls get the long timeout, like getEventPricing.
const lionAutoEvent = (eventId) => `/lion-auto/events/${encodeURIComponent(eventId)}`;
export const getLionAutoGroups = (eventId) => api.get(`${lionAutoEvent(eventId)}/groups`);

// body { name, criteria } (criteria.sections = the group) → 201 { group }. A
// section may be in several groups of the event. A new group starts with
// Auto off. Sent with no ranks (criteria.ladderIds empty), it comes back
// ranked in Smart sort order (its members, up to 500), read from the
// event's (cached) Skybox pricing: 404 PRICING_NOT_FOUND or 503 (Skybox)
// save nothing. Ranked by hand, it is saved as sent. Behind the per-user
// skyboxHeavy limiter (429).
export const createLionAutoGroup = (eventId, { name, criteria }) =>
  api.post(`${lionAutoEvent(eventId)}/groups`, { name, criteria });

// body: any of { name, criteria, autoEnabled } (an omitted key stays as
// saved) → { group }. autoEnabled is the group's Auto switch: on is
// admin-only (403 AUTO_ADMIN_ONLY) and needs a floor > 0: the group's own
// (criteria.ladderFloorPct), else the show's, else the artist's (400
// AUTO_NEEDS_FLOOR, also when clearing the floor of an Auto group that
// inherits none).
export const updateLionAutoGroup = (id, { name, criteria, autoEnabled } = {}) =>
  api.put(`/lion-auto/groups/${encodeURIComponent(id)}`, { name, criteria, autoEnabled });

// The group's "needs approval" queue (our listings that joined it after it
// was built). body { action: 'approve' | 'dismiss', inventoryIds, beforeId?, smart? }
// → { group, pending, approved?, placedLast? | dismissed?, skipped }. Approve
// with smart (Approve / Approve all; sent only when true, never with
// beforeId) slots each one in at its Smart sort spot in the ladder
// (criteria.ladderIds: just before the first ranked listing that sorts after
// it; the ranks after it move up one), read from the event's (cached) Skybox
// pricing: 404 PRICING_NOT_FOUND or 503 (Skybox) approve nothing;
// placedLast = the approved ids with no listing in that read, ranked last
// ([] when none). With beforeId (At rank…; sent only when set) it goes just
// before that ranked listing (the one shown at the rank picked: it and the
// ranks above it move up one); with neither (the last rank picked) it is
// ranked last and nothing else moves. Those two and dismiss (only clears the
// flag: still a member, unranked) are DB only, no Skybox call. Behind the
// per-user skyboxHeavy limiter (429).
// 400 { error, details } on a bad body (PENDING_INVALID) or a full ladder;
// 409 NOT_PENDING { pending } when they no longer wait (someone else handled
// them first); 409 RANKING_CHANGED { pending } when beforeId is no longer in
// the ladder (nothing written).
export const reviewLionAutoPending = (id, { action, inventoryIds, beforeId, smart } = {}) =>
  api.post(`/lion-auto/groups/${encodeURIComponent(id)}/pending`, {
    action, inventoryIds, ...(beforeId != null ? { beforeId } : {}), ...(smart ? { smart: true } : {}),
  });

export const deleteLionAutoGroup = (id) =>
  api.delete(`/lion-auto/groups/${encodeURIComponent(id)}`);

// Import from Lion Checker, step 1: what this show's Lion Checker filters
// would create or update (no write). The server finds the show by the
// event's VividSeats production id (vividUrl: the page's link, a fallback
// when Skybox has none), reads its filters from Lion Checker's database and
// the event's (cached) Skybox pricing, hence skyboxHeavy and the long
// timeout. Each proposal counts our listings it holds today (ownMatches,
// ownSeats); a create that holds none is skipped NO_LISTINGS unless
// includeEmpty (sent as includeEmpty=1, only when true). → { eventId,
// vividProductionId, pricingAgeMs, show, proposals, orphans, warnings,
// counts, groupsLeft, includeEmpty }. 503 CHECKER_DISABLED (not set up),
// 502 CHECKER_UNAVAILABLE, 404 CHECKER_SHOW_NOT_FOUND { reason,
// vividProductionId } or PRICING_NOT_FOUND, 503 (Skybox), 429.
export const getLionAutoCheckerPreview = (eventId, { vividUrl, includeEmpty } = {}) =>
  api.get(`${lionAutoEvent(eventId)}/checker-import`, {
    params: {
      ...(vividUrl ? { vividUrl } : {}),
      ...(includeEmpty ? { includeEmpty: 1 } : {}),
    },
    timeout: 60000,
  });

// Step 2: body { selections: [{ checkerEventId?, filterIndex, sig }],
// vividUrl?, includeEmpty? } (the preview's ticked proposals, each with its
// show; no checkerEventId = the primary show; includeEmpty only when true,
// as the preview had it) → 201 { eventId, show, shows, created, updated,
// skipped } (200 when there was nothing to do). The server recomputes each
// proposal: 409 CHECKER_STALE { stale, preview } when a filter changed on
// Lion Checker since the preview; 400 CHECKER_IMPORT_INVALID / GROUP_INVALID
// / CRITERIA_INVALID / AUTO_NEEDS_FLOOR { details }. Writes our groups
// only, never a Skybox price.
export const importLionAutoChecker = (eventId, { selections, vividUrl, includeEmpty } = {}) =>
  api.post(
    `${lionAutoEvent(eventId)}/checker-import`,
    { selections, ...(vividUrl ? { vividUrl } : {}), ...(includeEmpty ? { includeEmpty: true } : {}) },
    { timeout: 60000 }
  );

// Lion Auto templates: a show's groups saved once and applied to the
// artist's other dates (each section matched by name on that show). The
// list and the writes are DB only. → TemplateView { id, name, performerName,
// venue, version, sourceEventId, groups: [{ key, name, sections: [name],
// minQuantity, maxQuantity, rowsInclude, notesMode, notesTerms,
// ladderFloorPct }], linkedHere (this show's groups linked to it),
// updatedByName, updatedAt }.
// List → { eventId, performerName|null, venue|null, canEdit, templates }
// (the show's performer's templates, newest first; [] and canEdit false
// when the performer is not known).
const LION_AUTO_TEMPLATES = '/lion-auto/templates';
const lionAutoTemplate = (id) => `${LION_AUTO_TEMPLATES}/${encodeURIComponent(id)}`;
export const getLionAutoTemplates = (eventId) => api.get(LION_AUTO_TEMPLATES, { params: { eventId } });

// The performer's upcoming shows we hold listings at, plus upcoming shows
// that already have linked groups, in date order (pricer-scoped) → {
// templateId, version, shows: [{ eventId, eventName, eventDate (venue-local),
// venue, sameVenue, isSource, linked, linkedAuto, linkedVersion|null (the
// lowest version stamped on its linked groups), otherGroups, otherAuto }] }.
export const getLionAutoTemplateShows = (id) => api.get(`${lionAutoTemplate(id)}/shows`);

// Save, update and delete need the artist floor's permission (403
// ARTIST_SCOPE). Save: body { eventId, name, groupIds } → 201 { template,
// linked: [groupId] }. Update re-saves from a show: body { eventId,
// groupIds, name? } → { template, linked, unlinked } (version + 1; this
// show's groups linked to it but not in groupIds are unlinked). Delete → {
// id, unlinked: n } (its groups stay, as hand-made groups). 400
// TEMPLATE_INVALID { details }, 404 NOT_FOUND, 409 ARTIST_UNKNOWN.
export const saveLionAutoTemplate = ({ eventId, name, groupIds }) =>
  api.post(LION_AUTO_TEMPLATES, { eventId, name, groupIds });
export const updateLionAutoTemplate = (id, { eventId, groupIds, name }) =>
  api.put(lionAutoTemplate(id), { eventId, groupIds, ...(name != null ? { name } : {}) });
export const deleteLionAutoTemplate = (id) => api.delete(lionAutoTemplate(id));

// Apply to ONE show: body { eventId, version (the one the modal loaded) } →
// { eventId, templateId, version, created, updated (GroupViews), unchanged,
// skipped [{ key, name, reason: NO_SECTIONS|GROUP_LIMIT }], missingSections
// [{ key, name, sections }], orphans [{ id, name }], moved [{ inventoryId,
// fromGroupId, toGroupId, ranked }], missed [inventoryId] }. Reads the show's
// (cached) Skybox pricing, hence skyboxHeavy (429), 503 retryable and the
// long timeout. 409 TEMPLATE_STALE { version } (re-saved since), 409
// TEMPLATE_WRONG_ARTIST, 400 AUTO_NEEDS_FLOOR { details }. Writes our groups
// only, never a Skybox price.
export const applyLionAutoTemplate = (id, { eventId, version }) =>
  api.post(`${lionAutoTemplate(id)}/apply`, { eventId, version }, { timeout: 60000 });

// The event's floors (% of each listing's cost; DB only, no Skybox call): a
// group with no floor of its own (ladderFloorPct 0) uses the show floor,
// else the artist floor (the Skybox performer, every show of it). → {
// eventId, performerName|null, artist|null, show|null (each { pct,
// updatedByName, updatedAt }), inherited { pct, source: 'show'|'artist'|null
// }, canEditArtist, groups { here { follow, own, autoFollowing, auto },
// artist { own, autoFollowing, auto, showFloors }|null } } (auto: every Auto
// group in scope; showFloors: the artist's upcoming shows with a show floor).
export const getLionAutoFloors = (eventId) => api.get(`${lionAutoEvent(eventId)}/floors`);

// body: either or both of { artistPct, showPct } (0.01..500, 2 decimals;
// null clears, an omitted key stays) → the GET shape. 400 FLOOR_INVALID {
// details }, 403 ARTIST_SCOPE (not assigned to this artist), 409
// ARTIST_UNKNOWN (no performer for the show), 409 FLOOR_IN_USE { groups: [{
// id, name, eventId }] } (a clear would leave an Auto group with no floor).
// applyToAll (one floor above 0): the groups in scope with their own floor,
// and for the artist every upcoming show's show floor, are cleared so they
// follow it → + applied { groups: [ids], shows: [eventIds] }; 409
// FLOOR_GROUP_REFUSED { group: { id, name, eventId } } when a group can't
// take it (nothing saved). repriceNow (admin-like, else 403 ADMIN_ONLY
// before any write): the Auto groups whose floor changed re-price next → +
// reprice { queued, moved, notAuto, enabled, dryRun, status (the runner's,
// null when failed), failed? (the floors saved, the queue did not) }. Each
// flag is sent only when true.
export const saveLionAutoFloors = (eventId, { artistPct, showPct, applyToAll, repriceNow } = {}) =>
  api.put(`${lionAutoEvent(eventId)}/floors`, {
    artistPct,
    showPct,
    ...(applyToAll === true ? { applyToAll: true } : {}),
    ...(repriceNow === true ? { repriceNow: true } : {}),
  });

// scope 'show' | 'artist': the groups in scope with a floor of their own get
// 0 (they follow); an Auto group that would be left with none is skipped.
// The artist scope covers every show of the artist (its show floors stay)
// and needs the artist floor's permission. → { cleared, skipped, floors }.
export const followLionAutoFloors = (eventId, scope) =>
  api.post(`${lionAutoEvent(eventId)}/floors/follow`, { scope });

// The event's "Reprice after a sale" (any staff; DB only, no Skybox call):
// this show's setting wins, else its artist's (the Skybox performer, every
// show of it), else the runner's default. → { default: boolean, show:
// true|false|null, artist: { name, value: true|false|null }|null (null = the
// performer is not known), effective: { on, source: 'show'|'artist'|'default'
// }, canEdit (admin-like) }.
export const getLionAutoSaleRefill = (eventId) => api.get(`${lionAutoEvent(eventId)}/sale-refill`);

// Admin-like only (else 403 ADMIN_ONLY). body: either or both of { show,
// artist }, each true | false | null (null = back to the default; an omitted
// key stays) → the GET shape. 400 SALE_REFILL_INVALID { details }, 409
// ARTIST_UNKNOWN (no performer for the show).
export const saveLionAutoSaleRefill = (eventId, { show, artist } = {}) =>
  api.put(`${lionAutoEvent(eventId)}/sale-refill`, { show, artist });

// Unsaved draft { criteria, groupId? } → MatchResult, body forwarded as is.
// groupId: the saved group being edited (its saved criteria give way to
// these), omitted for a new group (matched after every saved one). A
// listing of ours is in one group per event, so the answer depends on the
// other saved groups too: one holding a listing keeps it, and it comes back
// in membersExcluded as GROUP. Read-only: a preview records no listing's
// group.
export const matchLionAutoPreview = (eventId, body, { maxAge } = {}) =>
  api.post(`${lionAutoEvent(eventId)}/match`, body,
    { timeout: 200000, ...(maxAge ? { params: { maxAge } } : {}) });

// Every saved group of the event → { eventId, pricingAgeMs, results: { "<groupId>": MatchResult } }
export const getLionAutoGroupMatches = (eventId, { maxAge } = {}) =>
  api.get(`${lionAutoEvent(eventId)}/groups/match`,
    { timeout: 200000, ...(maxAge ? { params: { maxAge } } : {}) });

// Price ladder of a group's ranked listings: { criteria (the full cleaned
// criteria, ladder keys included), groupId? (a saved group: these criteria
// stand in for its saved ones among the event's groups; none = a new group),
// resume? } → LadderResult (its inOtherGroups is always empty now: a listing
// is in one group per event). The server asks Skybox's what-if
// for each rung, one call at a time, and never writes; applying goes through
// bulkUpdatePrices. It caps a run at ~45 s and answers PARTIAL rather than
// time out (the 180 s timeout here is only headroom); resume { afterRank,
// prevL } continues such a run: only the rungs after afterRank come back,
// the first one targeting prevL + step.
export const calculateLionAutoLadder = (eventId, { criteria, groupId = null, resume = null }) =>
  api.post(`${lionAutoEvent(eventId)}/ladder`,
    { criteria, ...(groupId != null ? { groupId } : {}), ...(resume ? { resume } : {}) },
    { timeout: 180000 });

// Lion Auto's scheduled runner (admin-like only; DB and in-memory state, no
// Skybox read). Status → { enabled, dryRun, status (stopped | disabled |
// idle | working | paused_auth | no_session | paused_room), lastTickAt,
// lastRunAt, lastError, pausedSince, session { configured, label, live },
// config { enabled, dryRun, maxChangePct, maxWrites (each a number, or null
// = no limit, the default), cadence { over6wMs, within6wMs, within4wMs,
// within2wMs, within7dMs, within3dMs, within24hMs, within3hMs, startedMs }
// (ms per tier, timed to the show's start in the venue's time zone) },
// sinceBoot { runs, repriced,
// failed, dryRuns, checks (unchanged runs that skipped the ladder), ladders,
// auditOk, auditMiss }, groups [{ id, eventId, name, autoEnabledAt,
// autoLastRunAt, autoLastStatus, nextDueAt, presale (null, or { everyMs,
// until (ISO), timeZone, zoneKnown, night } while the show's presale
// schedule replaces its tier) }] (every event's Auto groups), presaleSync
// (the hourly Lion Checker presale-date read: { configured: false }, or
// { configured: true, lastSyncAt, lastError, events, unresolved }; older
// servers send none) }.
export const getLionAutoStatus = () => api.get('/lion-auto/auto/status');

// body: any of { enabled, dryRun, maxChangePct, maxWrites (null = no limit),
// cadence } → the config object; 400 { error, code, details } on junk
export const updateLionAutoConfig = (body) => api.put('/lion-auto/auto/config', body);

// Lion Auto's Discord alerts (super admin only). The webhook URL is
// write-only: status → { configured, keyPresent, hasWebhook, hint ("…a1b2",
// the token's last 4, or null), warnings [KEY_MISSING | WEBHOOK_UNREADABLE],
// updatedAt, lastSentAt, lastError, sent }. Save: a webhook URL, or null to
// remove it → the status; 400 BAD_WEBHOOK, 409 KEY_MISSING { error, code }.
// Test → 200 { ok, status?, error? } even when Discord refused it (error:
// HTTP_<status>, TIMEOUT, NETWORK_ERROR…); 400 NOT_CONFIGURED.
export const getLionAutoDiscord = () => api.get('/lion-auto/discord');
export const saveLionAutoDiscord = (webhookUrl) => api.put('/lion-auto/discord', { webhookUrl });
export const testLionAutoDiscord = () => api.post('/lion-auto/discord/test', {});

// One run of a group on Auto now, the runner's own path (a dry run when the
// runner is in dry run) → { group, summary }; 409 AUTO_DISABLED (master
// switch off) / AUTO_OFF (the group isn't on Auto). A run re-ladders through
// Skybox's what-if, hence the ladder's long timeout.
export const runLionAutoGroup = (id) =>
  api.post(`/lion-auto/groups/${encodeURIComponent(id)}/auto/run`, {}, { timeout: 180000 });

// Merge listings (Compare; staff, pricers only on events they can price).
// The plan is read from the inventory cache, no Skybox call → { groups,
// blocked, recent }. A merge is one group per call, body { eventId,
// inventoryIds, expectedListPrice } with the ids in the plan's order (highest
// B first: Skybox keeps the highest B) and the plan's B (0 = the group stays
// unpriced) → { merge: { id,
// mergedIds, seats, quantity, listPrice, warnings } } (id null = merged but
// not recorded: no Undo); 409 no longer valid / PRICE_CHANGED, 403
// LIMIT_EXCEEDED, 429, 503 { retryable } nothing sent, 502 Skybox refused,
// 502 { code: 'UNKNOWN_OUTCOME' } = Skybox didn't confirm. Undo → {
// restoredIds, warnings } (Skybox gives new ids); 409 already undone, 503 {
// retryable } not synced yet. Never re-sent automatically. The 90 s timeout
// outlasts nginx's 60 s, so a stuck write reads as its 504 (outcome unknown).
export const getMergePlan = (eventId) =>
  api.get(`/listing-merges/events/${encodeURIComponent(eventId)}/plan`);
export const mergeListings = (eventId, inventoryIds, expectedListPrice) =>
  api.post('/listing-merges', { eventId: Number(eventId), inventoryIds, expectedListPrice }, { timeout: 90000 });
export const undoListingMerge = (mergeId) =>
  api.post(`/listing-merges/${encodeURIComponent(mergeId)}/undo`, {}, { timeout: 90000 });

// Admin
export const getUsers = () => api.get('/admin/users');

export const createUser = (userData) => api.post('/admin/users', userData);

export const updateUser = (id, userData) => api.put(`/admin/users/${id}`, userData);

export const deleteUser = (id) => api.delete(`/admin/users/${id}`);

// Per-pricer Skybox sessions (super admin only). The password is write-only
// except for the explicit reveal, which the server logs.
export const getSkyboxSessions = () => api.get('/admin/skybox-sessions');
export const setSkyboxCredentials = (id, { email, password }) => api.put(`/admin/users/${id}/skybox-credentials`, { email, password });
export const revealSkyboxCredentials = (id) => api.get(`/admin/users/${id}/skybox-credentials`);
export const setSkyboxSessionTokens = (id, { token, refreshToken }) => api.put(`/admin/users/${id}/skybox-session`, { token, refreshToken });
export const clearSkyboxSession = (id) => api.delete(`/admin/users/${id}/skybox-session`);

// TicketMetric login + proxy (super admin only). Password and proxy URL are
// write-only; blank fields keep their current values.
export const getTicketMetricAdmin = () => api.get('/admin/ticketmetric');
export const updateTicketMetricAdmin = (updates) => api.put('/admin/ticketmetric', updates);
// Runs through the TicketMetric queue and waits up to ~50 s server-side
export const testTicketMetricAdmin = () => api.post('/admin/ticketmetric/test', {}, { timeout: 75000 });

// Data backbone (super admin only, Admin → Data): backfill jobs, the backfill
// Skybox logins, Lion Auto's Skybox login, the data-quality report. A 503 BACKFILL_UNAVAILABLE means the
// server has no backfill engine yet.
const datasetJob = (job) => `/dataset/jobs/${encodeURIComponent(job)}`;
export const getDatasetStatus = () => api.get('/dataset/status');
export const pauseDatasetJob = (job) => api.post(`${datasetJob(job)}/pause`);
export const resumeDatasetJob = (job) => api.post(`${datasetJob(job)}/resume`);
// Answers within ~2 s: 200 when the batch already finished, 202 while it continues server-side
export const runDatasetJob = (job) => api.post(`${datasetJob(job)}/run`, {}, { timeout: 30000 });
// body { enabled?: boolean, gapMs?: integer ≥ 500, slotCap?: { limit, windowMs } (listing_history only) }
export const updateDatasetJob = (job, body) => api.put(datasetJob(job), body);
// userIds [] clears the backfill logins
export const setDatasetSession = (userIds) => api.put('/dataset/session', { userIds: Array.isArray(userIds) ? userIds : [] });
// Lion Auto's Skybox logins (the unattended runner's, at most 5): the whole
// list; [] clears them, a login left out goes back to the pool →
// { success, autoUserIds, autoUserId (the first), sessions }. 400 USER_ACTIVE /
// NO_SKYBOX_SESSION / SLOT_IN_USE (a backfill login) / too many, 404
// USER_NOT_FOUND, each with the server's own message; 500 { error, detail,
// autoUserIds } when it failed part-way (autoUserIds = what is saved now).
export const setDatasetAutoSession = (userIds) =>
  api.put('/dataset/auto-session', { userIds: Array.isArray(userIds) ? userIds : [] });
export const getDatasetQuality = () => api.get('/dataset/quality');
// Recomputes the report (~1-2 s server-side); 429 within a minute of the last refresh
export const refreshDatasetQuality = () => api.post('/dataset/quality/refresh', {}, { timeout: 60000 });

// Reports (/reports, super admin only): local data, never a Skybox call;
// series bucket ids are Eastern wall clock. params { dateFrom, dateTo }
// (Eastern YYYY-MM-DD) plus bucket? (sales, purchases); 400 { error } for a
// bad range or bucket, 403 for anyone but a super admin.
export const getSalesReport = (params) => api.get('/reports/sales', { params });
// The range's market sales one by one, newest first (dateFrom, dateTo, limit?
// (at most 100) / offset?): the sale's total, Skybox's profit and the buyer's
// name; refreshes a live range from Skybox like /reports/sales.
export const getRecentSales = (params) => api.get('/reports/sales/recent', { params });
export const getPurchasesReport = (params) => api.get('/reports/purchases', { params });
// The purchases table (local data only): artists (sort? / dir? / q? / limit?
// / offset?, see treeParams), an artist's shows, one per event (performer =
// the artist row's key), an artist's listings for a show (performer, event =
// the show row's key, limit? / offset?). A key of '' is the unknown performer
// or the lines with no event and must still be sent: axios drops only null /
// undefined params.
export const getPurchaseArtists = (params) => api.get('/reports/purchases/artists', { params });
export const getPurchaseShows = (params) => api.get('/reports/purchases/shows', { params });
export const getPurchaseListings = (params) => api.get('/reports/purchases/listings', { params });
// The range's purchase lines, newest bought first (dateFrom, dateTo, buyer?,
// limit? / offset?): no buyer = every line; buyer = a buyers row's key (that
// tag's lines; '' = the lines with no buyer tag, sent as is). And the buyers
// (dateFrom, dateTo): one row per ticked buyer tag, plus key '' last.
export const getPurchaseLines = (params) => api.get('/reports/purchases/lines', { params });
export const getPurchaseBuyers = (params) => api.get('/reports/purchases/buyers', { params });

export const getPerformers = () => api.get('/admin/performers');

export const getPerformerTiers = () => api.get('/admin/performer-tiers');

export const updatePerformerTiers = (assignments) =>
  api.put('/admin/performer-tiers', { assignments });

export const updatePerformerNotes = (notes) =>
  api.put('/admin/performer-notes', { notes });

export const getAdminEventNotes = () => api.get('/admin/event-notes');

export const getActivityLog = (params) =>
  api.get('/admin/activity-log', { params });

export const getStats = () => api.get('/admin/stats');

// Tag colors
export const getTagColors = () => api.get('/inventory/tag-colors');

export const getInventoryTags = () => api.get('/inventory/tags');

export const getAdminTagColors = () => api.get('/admin/tag-colors');

export const updateTagColors = (tagColors) =>
  api.put('/admin/tag-colors', { tagColors });

export const deleteTagColor = (tagName) =>
  api.delete(`/admin/tag-colors/${encodeURIComponent(tagName)}`);

// Buyer tags (admin; they drive Reports → Purchases by tag): GET → string[];
// PUT { isBuyer } → { tag, isBuyer }, 400 { error } for a name with
// whitespace or a comma, an empty or too long one, or a non-boolean isBuyer
export const getBuyerTags = () => api.get('/admin/buyer-tags');

export const setBuyerTag = (tag, isBuyer) =>
  api.put(`/admin/buyer-tags/${encodeURIComponent(tag)}`, { isBuyer });

// Note requests (buyer public-note requests + Skybox note sync)
// Any role
export const getNoteCategories = (all) =>
  api.get('/note-requests/categories', { params: all ? { all: 1 } : {} });

export const createNoteRequest = (body) => api.post('/note-requests', body);

// params: { scope: 'all'|'open', q, limit, offset } — full history, newest first
export const getMyNoteRequests = (params) =>
  api.get('/note-requests/mine', { params: typeof params === 'string' ? { scope: params } : (params || {}) });

export const getNoteRequest = (id) => api.get(`/note-requests/${id}`);

export const cancelNoteRequest = (id) => api.post(`/note-requests/${id}/cancel`, {});

// Photo on a request (owner or staff). Raw image body, not FormData: the
// instance default Content-Type is JSON, so the image type must be explicit
// or the server would not read the body as an image. utils/imagePrep keeps
// blobs ≤ 900 KB (under nginx's default 1 MB limit).
export const uploadNoteAttachment = (id, blob, name) =>
  api.post(`/note-requests/${id}/attachments`, blob, {
    headers: { 'Content-Type': blob.type },
    params: { name },
    timeout: 60000,
  });

// Uploader or admin-like only (server enforces; 403 otherwise)
export const deleteNoteAttachment = (id, attId) =>
  api.delete(`/note-requests/${id}/attachments/${attId}`);

// Staff (admin/pricer)
export const getNoteRequests = (params = {}) => api.get('/note-requests', { params });

export const previewNoteRequest = (id) => api.get(`/note-requests/${id}/preview`);

// Serial server-side (one Skybox GET+PUT per listing, 1.1s gap) — allow 2 min
export const applyNoteRequest = (id, body = {}) =>
  api.post(`/note-requests/${id}/apply`, body, { timeout: 120000 });

export const linkNoteRequestListing = (id, inventoryId) =>
  api.post(`/note-requests/${id}/link`, { inventoryId });

export const unlinkNoteRequestListing = (id, listingId) =>
  api.delete(`/note-requests/${id}/listings/${listingId}`);

export const resolveNoteRequest = (id, note) =>
  api.post(`/note-requests/${id}/resolve`, note ? { note } : {});

export const searchNoteListings = (q) =>
  api.get('/note-requests/search-listings', { params: { q } });

export const getNoteSyncStatus = () => api.get('/note-requests/status');

export const getNoteSyncRecentWrites = () => api.get('/note-requests/recent-writes');

// Admin
export const getNoteSyncConfig = () => api.get('/note-requests/config');

export const updateNoteSyncConfig = (updates) => api.put('/note-requests/config', updates);

export const saveNoteCategories = (categories) =>
  api.put('/note-requests/categories', { categories });

export const runNoteSyncTick = () => api.post('/note-requests/sweep', {}, { timeout: 120000 });

export default api;
